import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Box, Typography, Button, Stack, Container } from "@mui/joy";
import { getAllApplicants, getEmployer, getEmployerPosition } from "../../../apiconfig/api";
import { useMetaTags } from "../../../hooks/useMetaTags";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import S3_URL from "../../../utils/s3";
import {
  LoadingState,
  ErrorState,
  ContentLayout,
  VideoApplicationModal,
  ContactInfoModal,
} from "../common/components";
import { useFileUpload } from "hooks/useFileUpload";
import { useSession } from "context/SessionContext";

const PositionListing = () => {
  const { company, position } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { fileInputRef, handleFileChange, handleRequestGallery } =
    useFileUpload(() => {}, () => {});
  
  const isHiringRoute = location.pathname.startsWith('/hiring/');
  
  const [employer, setEmployer] = useState<any>(null);
  const [employerPosition, setEmployerPosition] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [searchParams] = useSearchParams();
  const zipcode = searchParams.get('zipcode') || '';
  const rolesParam = searchParams.get('roles') || '';
  const employerPositionsParam = searchParams.get('employerPositions') || '';
  const roles = useMemo(() => 
    rolesParam ? rolesParam.split(',').map(p => p.trim()).filter(Boolean) : [], 
    [rolesParam]
  );
  const employerPositions = useMemo(() => 
    employerPositionsParam ? employerPositionsParam.split(',').map(p => p.trim()).filter(Boolean) : [], 
    [employerPositionsParam]
  );
  
  // For the filter modal, always include the current position ID as selected
  const currentEmployerPositions = useMemo(() => {
    if (!employerPosition?.id) return employerPositions;
    
    const positions = [employerPosition.id];
    // Add any additional positions from URL that aren't the current position
    employerPositions.forEach(pos => {
      if (pos !== employerPosition.id && !positions.includes(pos)) {
        positions.push(pos);
      }
    });
    return positions;
  }, [employerPosition?.id, employerPositions]);
  
  const { sessionEmployerId } = useSession();

  // Create fetch function for infinite scroll
  const fetchCandidates = useCallback(async (page: number, per_page: number) => {
    if (!employerPosition?.id) {
      throw new Error("Position ID not available");
    }
    
    // Always include the current position ID, merge with any additional employer positions from URL
    const allEmployerPositions = [employerPosition.id];
    if (employerPositions.length > 0) {
      // Add any additional positions from URL that aren't already included
      employerPositions.forEach(pos => {
        if (!allEmployerPositions.includes(pos)) {
          allEmployerPositions.push(pos);
        }
      });
    }
    
    return await getAllApplicants({
      zipcode,
      globalPositionIds: roles.length > 0 ? roles : undefined,
      submittedToEmployerIds: sessionEmployerId ? [sessionEmployerId] : undefined,
      submittedToEmployerPositionIds: allEmployerPositions,
      page,
      per_page,
      forcePrivate: true
    });
  }, [employerPosition?.id, zipcode, roles, employerPositions, sessionEmployerId]);

  // Infinite scroll hook for people
  const {
    data: candidates,
    loading: candidatesLoading,
    error: candidatesError,
    hasMore: hasMoreCandidates,
    loadMore: loadMoreCandidates,
    total: totalCandidates
  } = useInfiniteScroll({
    fetchFunction: fetchCandidates,
    perPage: 5,
    enabled: isHiringRoute && !!employerPosition?.id
  });

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (!company || !position) {
        setError("Missing company or position information");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch employer data
        const employerResponse = await getEmployer(company);
        setEmployer(employerResponse.data.employer);

        // Fetch position data
        const positionResponse = await getEmployerPosition(company, position);
        setEmployerPosition(positionResponse.data.position);

        // People will be loaded via infinite scroll hook when employer is viewing
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load position information. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [company, position]);

  // Cleanup on unmount - stop all videos
  useEffect(() => {
    return () => {
      // Find and stop all video elements on unmount
      const videos = document.querySelectorAll('video');
      videos.forEach(video => {
        try {
          video.pause();
          video.removeAttribute('src');
          video.load();
        } catch (e) {
          // Ignore errors during cleanup
        }
      });
    };
  }, []);

  const handleApplyNow = () => {
    localStorage.removeItem("applicantData");
    setVideoPlaying(false); // Pause the intro video
    setContactModalOpen(true);
  };

  const updateApplicantData = () => {
    const existingData = localStorage.getItem('applicantData');
    const parsedData = existingData ? JSON.parse(existingData) : {};
    
    const updatedData = {
      ...parsedData,
      submittedToEmployerId: employer.uuid,
      submittedToEmployerPositionId: employerPosition.id,
      employerName: employer.companyName,
      positionName: employerPosition.displayNameOverride,
    };

    localStorage.setItem('applicantData', JSON.stringify(updatedData));
  };

  const handleRecordNow = () => {
    updateApplicantData();
    navigate('/apply/record');
  };

  const handleUploadFromDevice = () => {
    updateApplicantData();
    handleRequestGallery();
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setVideoPlaying(true); // Resume video when modal closes
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setVideoPlaying(true); // Resume video when modal closes
  };

  const handleContactFormSubmit = (formData: any) => {
    // Store contact information in localStorage
    const applicantData = {
      fullName: formData.fullName,
      phoneNumber: formData.phoneNumber,
      zipCode: formData.zipCode,
      agreeToTerms: formData.agreeToTerms,
      applicantUuid: formData.applicantUuid,
      submittedToEmployerId: employer.uuid,
      submittedToEmployerPositionId: employerPosition.id,
      employerName: employer.companyName,
      positionName: employerPosition.displayNameOverride,
    };
    
    localStorage.setItem('applicantData', JSON.stringify(applicantData));
    
    // Close contact modal and open video application modal
    setContactModalOpen(false);
    setModalOpen(true);
  };

  const handleNavigateBack = () => {
    navigate(`/apply/${company}`);
  };

  // Custom filter handler for employer position changes - redirect to /hiring/people
  const handleFilterApply = (filters: { zipcode: string; roles: string[]; employerPositions: string[] }) => {
    // Check if employer positions actually changed
    const currentEmployerPositionsSet = new Set(currentEmployerPositions);
    const newEmployerPositionsSet = new Set(filters.employerPositions);
    
    const employerPositionsChanged = 
      currentEmployerPositionsSet.size !== newEmployerPositionsSet.size ||
      Array.from(currentEmployerPositionsSet).some(pos => !newEmployerPositionsSet.has(pos));
    
    // Only redirect if employer positions changed
    if (employerPositionsChanged) {
      // Build URL parameters
      const params = new URLSearchParams();
      
      if (filters.zipcode && filters.zipcode.trim() !== '') {
        params.set('zipcode', filters.zipcode);
      }
      
      if (filters.roles && filters.roles.length > 0) {
        params.set('roles', filters.roles.join(','));
      }
      
      if (filters.employerPositions && filters.employerPositions.length > 0) {
        params.set('employerPositions', filters.employerPositions.join(','));
      }
      
      // Redirect to /hiring/people with the new parameters
      const searchParamsString = params.toString();
      const targetUrl = searchParamsString ? `/hiring/candidates?${searchParamsString}` : '/hiring/candidates';
      navigate(targetUrl);
    } else {
      // If employer positions didn't change, just update local URL parameters for other filters
      const params = new URLSearchParams(searchParams);
      
      // Update or remove zipcode
      if (filters.zipcode && filters.zipcode.trim() !== '') {
        params.set('zipcode', filters.zipcode);
      } else {
        params.delete('zipcode');
      }
      
      // Update or remove roles
      if (filters.roles && filters.roles.length > 0) {
        params.set('roles', filters.roles.join(','));
      } else {
        params.delete('roles');
      }
      
      // Update URL parameters without navigation
      const newSearchParams = new URLSearchParams();
      params.forEach((value, key) => {
        newSearchParams.set(key, value);
      });
      
      // Update search params (this will trigger data refetch via useEffect in the infinite scroll)
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  };

  // Generate poster image URL for social sharing
  const getPosterImageUrl = (videoId: string) => {
    return `${S3_URL}processed/${videoId}/frames/${videoId}-.0000000.jpg`;
  };

  // Loading state
  if (loading) {
    return <LoadingState />;
  }

  // Error state
  if (error || !employer || !employerPosition) {
    return (
      <ErrorState 
        error={error} 
        company={company} 
        onNavigateBack={handleNavigateBack} 
      />
    );
  }
  // Check if position is inactive and user is not an employer viewing their own position
  if (employerPosition.visibility === 'inactive' && !isHiringRoute) {
    return (
      <Box sx={{ 
        minHeight: "100vh", 
        bgcolor: "#f4f4f4",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4
      }}>
        <Container maxWidth="sm">
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Typography level="h2" sx={{ fontWeight: 700, color: '#1A1B1D' }}>
              This post is unavailable
            </Typography>
            <Typography level="body-lg" sx={{ color: '#7B7F85' }}>
              Please see other posts from this employer.
            </Typography>
            <Button
              variant="solid"
              onClick={() => navigate(`/apply/${company}`)}
              sx={{
                height: '56px',
                fontSize: '18px',
                fontWeight: 600,
                px: 4,
                backgroundColor: '#2e2d7e',
                '&:hover': { backgroundColor: '#1e1d6e' }
              }}
            >
              Back to {employer.companyName}
            </Button>
          </Stack>
        </Container>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      minHeight: "100vh", 
      bgcolor: "#f4f4f4",
      display: "flex",
      flexDirection: "column"
    }}>
      <ContentLayout 
        employer={employer}
        employerPosition={employerPosition}
        onApplyNow={handleApplyNow}
        isEmployerView={isHiringRoute}
        people={isHiringRoute ? candidates : []}
        peopleLoading={isHiringRoute ? candidatesLoading : false}
        peopleError={isHiringRoute ? candidatesError : undefined}
        hasMorePeople={isHiringRoute ? hasMoreCandidates : false}
        onLoadMorePeople={isHiringRoute ? loadMoreCandidates : undefined}
        totalPeople={isHiringRoute ? totalCandidates : 0}
        customFilterHandler={isHiringRoute ? handleFilterApply : undefined}
        currentEmployerPositions={isHiringRoute ? currentEmployerPositions : undefined}
        videoPlaying={videoPlaying}
        setVideoPlaying={setVideoPlaying}
      />
      
      <ContactInfoModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        onSubmit={handleContactFormSubmit}
        companyName={employer?.companyName || ''}
        positionName={employerPosition?.displayNameOverride || ''}
        employerUuid={employer?.uuid || ''}
        employerPositionId={employerPosition?.id || ''}
      />
      
      <VideoApplicationModal
        open={modalOpen}
        onClose={handleCloseModal}
        onRecordNow={handleRecordNow}
        onUploadFromDevice={handleUploadFromDevice}
        companyName={employer?.companyName || ''}
        positionName={employerPosition?.displayNameOverride || ''}
      />
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="video/*"
        style={{ display: "none" }}
      />
    </Box>
  );
};

export default PositionListing;